/**
 * Anonymous toggle for a Fellows Corner mentorship inquiry.
 *
 * Mirrors the general inquiry's anonymous checkbox but is typed to the
 * mentorship form and uses question-oriented copy: the recipient is a single
 * mentor, not a fan-out of experts, so there is no fan-out concept here.
 */

import { CheckIcon } from 'lucide-react-native';
import { Controller, Control } from 'react-hook-form';

import { Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel } from '@/components/ui/checkbox';
import { FormControl } from '@/components/ui/form-control';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import type { MentorshipFormData } from '@/hooks/useMentorshipInquiryForm';
import { segmentClient } from '@/util/analytics';

type MentorshipAnonymousCheckboxProps = {
  control: Control<MentorshipFormData>;
  isDisabled?: boolean;
};

export function MentorshipAnonymousCheckbox({
  control,
  isDisabled = false,
}: MentorshipAnonymousCheckboxProps) {
  return (
    <Controller
      name="anonymous"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControl isDisabled={isDisabled}>
          <VStack className="rounded-md bg-background-50 p-3">
            <Checkbox
              value={value?.toString() || ''}
              onChange={(checked) => {
                onChange(checked);

                segmentClient
                  .track(ANALYTICS_EVENTS.ANONYMOUS_INQUIRY_TOGGLED, { enabled: checked })
                  ?.catch(() => {});
              }}
              isChecked={value}
              size="sm"
              isDisabled={isDisabled}
              aria-label="Ask anonymously"
              testID="mentorship-anonymous-checkbox">
              <CheckboxIndicator>
                <CheckboxIcon as={CheckIcon} />
              </CheckboxIndicator>
              <CheckboxLabel>
                <>Ask anonymously</>
              </CheckboxLabel>
            </Checkbox>
          </VStack>

          <Text size="xs" className="mt-2 font-normal text-typography-500">
            Your mentor will see your question but not your name or institution.
          </Text>
        </FormControl>
      )}
    />
  );
}
